// ── Bike specs and color matching for Settings › Website (1 Nov) ──────────────
//
// SPEC_GROUPS: the specs a shop can pick from, grouped the way a spec sheet
// reads, each with an example that shows in the empty box. Anything not here
// is added as a custom spec with its own name (Manny, 1 Nov: the first list
// was too short, and shops need their own entries too).
//
// STARTER_SPECS: the usual specs for each website category, added in one
// click as empty rows to fill in. Rows left empty are not saved.
//
// likelyColors: Lightspeed lists each color of a bike as its own item, so
// "Micro Ripper 12 Sparkling White" and "Micro Ripper 12 Silver Streak" arrive
// as two bikes. Same brand and the same first words of the name means it is
// probably the same model in another color, as long as the numbers in the
// names agree: "Coast Trail 20" and "Coast Trail 24", or "1SP" and "7SP", are
// different bikes. 27.5 and 29 are the exception, because Marin builds one
// model on 27.5" wheels in small sizes and 29" in large (Bolinas Ridge 2,
// Rift Zone 1).

export const SPEC_GROUPS = [
  { group: 'Frame and fit', specs: [
    ['Frame', 'Carbon, Super Boost 157mm'], ['Frame material', 'Aluminum'], ['Fork', 'Fox 36 Performance, 160mm'],
    ['Sizes', 'XS to XL'], ['Rider height', `5'4" to 6'2"`], ['Recommended age', '5 to 8 years'],
    ['Weight', '31.5 lb (size M)'], ['Max weight', '300 lb rider and cargo'], ['Tire clearance', 'Up to 29 x 2.6"'],
    ['Head tube angle', '64.5°'], ['Seat tube angle', '77°'], ['Reach', '475mm (size M)'], ['Stack', '625mm (size M)'],
    ['Chainstay length', '435mm'], ['Wheelbase', '1,230mm (size M)'], ['Color', 'Doctor Purple'],
  ] },
  { group: 'Suspension', specs: [
    ['Rear travel', '150mm'], ['Front travel', '160mm'], ['Rear shock', 'Fox Float X Performance'],
    ['Suspension design', 'DW-Link'], ['Lockout', 'Handlebar remote'],
  ] },
  { group: 'Drivetrain', specs: [
    ['Drivetrain', 'SRAM Eagle 70 Transmission'], ['Speeds', '1 x 12'], ['Gearing', '25/9'], ['Shifter', 'Shimano Deore'],
    ['Rear derailleur', 'Shimano Deore'], ['Front derailleur', 'Shimano 105'], ['Crankset', 'SRAM Eagle 70, 170mm'],
    ['Chainring', '32T'], ['Cassette', '10-52T'], ['Chain', 'Shimano 12-speed'], ['Bottom bracket', 'Threaded BSA'],
    ['Pedals', 'Flat pedals included'],
  ] },
  { group: 'Brakes', specs: [
    ['Brakes', 'Shimano 4-piston hydraulic disc'], ['Brake rotors', '203mm front, 180mm rear'],
  ] },
  { group: 'Wheels and tires', specs: [
    ['Wheel size', '29"'], ['Wheels', 'DT Swiss M1900'], ['Rims', 'Alloy, 30mm inner width'], ['Hubs', 'Shimano MT410'],
    ['Tires', 'Maxxis Minion DHF 29 x 2.5'], ['Front tire', 'Maxxis Assegai 29 x 2.5'], ['Rear tire', 'Maxxis Dissector 29 x 2.4'],
    ['Tubeless ready', 'Yes, rims taped and valves included'],
  ] },
  { group: 'Cockpit and seating', specs: [
    ['Handlebar', '800mm, 30mm rise'], ['Stem', '40mm'], ['Grips', 'Lock-on'], ['Bar tape', 'Cork'], ['Headset', 'Integrated'],
    ['Seatpost', '27.2mm alloy'], ['Dropper post', '170mm (size M)'], ['Saddle', 'WTB Volt'],
  ] },
  { group: 'E-bike', specs: [
    ['Motor', 'Shimano EP801, 85 Nm'], ['Battery', '630 Wh, removable'], ['Range', 'Up to 60 miles'],
    ['Class', 'Class 1, pedal assist to 20 mph'], ['Top assisted speed', '20 mph'], ['Throttle', 'Thumb throttle'],
    ['Display', 'Color display with USB-C'], ['Assist modes', 'Eco, Trail, Boost'], ['Charger', '4A'], ['Charge time', 'About 5 hours'],
  ] },
  { group: 'Extras', specs: [
    ['Fenders', 'Full coverage'], ['Rack', 'Rear rack, 55 lb'], ['Kickstand', 'Included'], ['Lights', 'Front and rear, integrated'],
    ['Bell', 'Included'], ['Chain guard', 'Included'], ['Mounts', 'Rack, fender and bottle mounts'],
    ['Included accessories', 'Training wheels'], ['Warranty', 'Lifetime frame warranty'],
  ] },
];

const EXAMPLES = new Map(SPEC_GROUPS.flatMap((g) => g.specs));
export const specExample = (label) => EXAMPLES.get(label) || '';
export const isKnownSpec = (label) => EXAMPLES.has(label);

export const STARTER_SPECS = {
  'Mountain': ['Frame', 'Fork', 'Rear shock', 'Rear travel', 'Front travel', 'Wheel size', 'Drivetrain', 'Brakes', 'Tires', 'Dropper post', 'Weight'],
  'Road & Gravel': ['Frame', 'Fork', 'Drivetrain', 'Brakes', 'Wheels', 'Tires', 'Tire clearance', 'Weight'],
  'Electric': ['Motor', 'Battery', 'Range', 'Class', 'Display', 'Drivetrain', 'Brakes', 'Tires', 'Weight'],
  'Commuter & Hybrid': ['Frame', 'Drivetrain', 'Brakes', 'Tires', 'Mounts', 'Weight'],
  'Kids': ['Wheel size', 'Recommended age', 'Rider height', 'Frame', 'Speeds', 'Brakes', 'Weight'],
  'BMX': ['Frame', 'Fork', 'Wheel size', 'Gearing', 'Brakes', 'Tires'],
};
export const starterFor = (category) => STARTER_SPECS[category] || ['Frame', 'Drivetrain', 'Brakes', 'Wheel size', 'Tires', 'Weight'];

const words = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9.\s]/g, ' ').split(/\s+/).filter(Boolean);
const numbers = (name) => words(name).filter((w) => /\d/.test(w) && w !== '27.5' && w !== '29').sort().join(' ');
const brandOf = (b) => String((b.content && b.content.brand) || b.brand || '').toLowerCase().trim();

// How many words two names share from the start.
export function sharedStart(a, b) {
  const x = words(a); const y = words(b);
  let n = 0;
  while (n < x.length && n < y.length && x[n] === y[n]) n++;
  return n;
}

// Bikes that are probably this bike in another color, best match first.
// Only bikes that are free to add: not this one, not already folded into a
// bike, and not carrying colors of their own.
export function likelyColors(bike, bikes) {
  const brand = brandOf(bike);
  return bikes
    .filter((b) => b.key !== bike.key && !b.mergedInto && !bikes.some((x) => x.mergedInto === b.key))
    .filter((b) => brandOf(b) === brand)
    .map((b) => ({ b, n: sharedStart(bike.name, b.name) }))
    .filter(({ b, n }) => n >= 2 && numbers(b.name) === numbers(bike.name))
    .sort((p, q) => q.n - p.n || p.b.name.localeCompare(q.b.name))
    .map(({ b }) => b);
}
